/**
 * The way back to a report, as it was left (#478).
 *
 * Адреси за заключване opens Settings → Адреси one address at a time. Somebody locking the
 * paid ones works down a filtered list, so the page they land on carries a link back to the
 * report with its period and filter, and the report opens on them rather than on its defaults.
 * The period lives in the reports area's memory, which leaving the area forgets — so it travels
 * in the link, written the way every report asks the server for it. So do the order, the page
 * and the search the list was left on, so the administrator comes back to the row they were at.
 *
 * And the row being locked travels the other way, by its key (`lockRow`): the settings page
 * can then ask the report what that row is made of, rather than trusting its words.
 */

import {
  addressesParams,
  addressSorts,
  importedViews,
  type AddressesView,
  type AddressSort,
  type ImportedView,
} from './addressesQuery';
import { periodPresets, type Period, type PeriodPreset } from './period';

/**
 * Who named an address, as the report filters by it — or `Imported`, the addresses the firm's
 * previous system handed over with the point it had them at, to be worked through and locked.
 */
export type SourceFilter = 'all' | 'Google' | 'Map' | 'Imported';

/** The link back to Адреси за заключване with this period and filter, and the view, order, page and search. */
export function addressesReport(
  period: Period,
  source: SourceFilter,
  view: Partial<Omit<AddressesView, 'source'>> = {},
): string {
  const query = new URLSearchParams({ preset: period.preset });

  if (period.preset === 'custom') {
    query.set('from', period.from);
    query.set('to', period.to);
  }

  for (const [name, value] of addressesParams({ ...view, source })) query.set(name, value);

  return `/reports/addresses?${query.toString()}`;
}

/** The order a report link carries; the default for anything else. */
export function sortFrom(params: URLSearchParams): AddressSort {
  const sort = params.get('sort');

  return (addressSorts as readonly string[]).includes(sort ?? '') ? (sort as AddressSort) : 'Bookings';
}

/** The view of Внесени a report link carries; the waiting ones for anything else. */
export function viewFrom(params: URLSearchParams): ImportedView {
  const view = params.get('view');

  return (importedViews as readonly string[]).includes(view ?? '') ? (view as ImportedView) : 'Waiting';
}

/** The page a report link carries, from 0; the first for anything that is not a whole number. */
export function pageFrom(params: URLSearchParams): number {
  const page = params.get('page');

  return page !== null && /^\d{1,6}$/.test(page) ? Number(page) : 0;
}

/** The search a report link carries, trimmed and kept short; empty for none. */
export function queryFrom(params: URLSearchParams): string {
  return (params.get('q') ?? '').trim().slice(0, 200);
}

/**
 * The row Settings → Адреси is opened to lock, as parameters beside the building and the
 * point: its key, the filter it was listed under and the dates the list was read over — what
 * asking the report for its members again takes. An imported row has no period.
 */
export function lockRow(key: string, source: SourceFilter, period: { from: string; to: string }): Record<string, string> {
  const row: Record<string, string> = { row: key };

  if (source !== 'all') row.rowSource = source;

  if (source !== 'Imported') {
    row.rowFrom = period.from;
    row.rowTo = period.to;
  }

  return row;
}

/** The row a link to Settings → Адреси carries, as `lockRow` wrote it; null when it carries none worth trusting. */
export interface LockedRow {
  key: string;
  source: SourceFilter;
  /** The dates the list was read over; null for an imported row, which has none. */
  from: string | null;
  to: string | null;
}

/** Reads back what `lockRow` wrote: a key that is an id, a known filter, and real days. */
export function rowFrom(params: URLSearchParams): LockedRow | null {
  const key = params.get('row');

  if (key === null || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key)) return null;

  const raw = params.get('rowSource');
  const source: SourceFilter = raw === 'Google' || raw === 'Map' || raw === 'Imported' ? raw : 'all';

  if (source === 'Imported') return { key, source, from: null, to: null };

  const from = params.get('rowFrom');
  const to = params.get('rowTo');
  const day = /^\d{4}-\d{2}-\d{2}$/;

  return from !== null && to !== null && day.test(from) && day.test(to) ? { key, source, from, to } : null;
}

/** The period a report link carries, or null when it carries none worth trusting. */
export function periodFrom(params: URLSearchParams): Period | null {
  const preset = params.get('preset');

  if (preset === null || !(periodPresets as readonly string[]).includes(preset)) return null;

  if (preset !== 'custom') {
    const today = new Date().toISOString().slice(0, 10);

    return { preset: preset as PeriodPreset, from: today, to: today };
  }

  const from = params.get('from');
  const to = params.get('to');
  const day = /^\d{4}-\d{2}-\d{2}$/;

  return from !== null && to !== null && day.test(from) && day.test(to) ? { preset: 'custom', from, to } : null;
}

/** The filter a report link carries; all, for anything else. */
export function sourceFrom(params: URLSearchParams): SourceFilter {
  const source = params.get('source');

  return source === 'Google' || source === 'Map' || source === 'Imported' ? source : 'all';
}

/**
 * A link back worth following: one of the console's own reports, never an address from
 * elsewhere that the query string was given.
 */
export function backFrom(params: URLSearchParams): string | null {
  const back = params.get('back');

  return back !== null && back.startsWith('/reports/') ? back : null;
}
